import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { Lock, Plus, ChevronDown, CalendarCheck } from 'lucide-react';
import { toast } from 'sonner';
import DictableTextarea from './DictableTextarea';
import ModuloConsenso from './ModuloConsenso';
import DatePickerField from '@/components/DatePickerField';

const OGGI = () => new Date().toISOString().slice(0, 10);
const it = (d) => d ? new Date(d).toLocaleDateString('it-IT') : '-';

export default function ContenzioniCard({ ospiteId, onSaved }) {
  const { hasRole } = useAuth();
  const [dati, setDati] = useState(null);
  const [defs, setDefs] = useState(null);
  const [nuova, setNuova] = useState(null);
  const [riv, setRiv] = useState(null);
  const [aperta, setAperta] = useState(null);
  const [salvando, setSalvando] = useState(false);
  const puoScrivere = hasRole('coordinatore', 'medico', 'infermiere');

  const carica = useCallback(() => {
    api.get(`/contenzioni/ospite/${ospiteId}`).then(r => setDati(r.data)).catch(() => {});
  }, [ospiteId]);

  useEffect(() => {
    carica();
    api.get('/contenzioni/definizioni').then(r => setDefs(r.data)).catch(() => {});
  }, [carica]);

  const crea = async () => {
    if (!nuova.tipo) return toast.error('Indica il tipo di contenzione');
    if (!nuova.motivazione.trim()) return toast.error('Indica la motivazione clinica');
    setSalvando(true);
    try {
      await api.post('/contenzioni', { ...nuova, ospite_id: ospiteId });
      toast.success('Contenzione registrata');
      setNuova(null); carica(); onSaved?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore nel salvataggio'); }
    setSalvando(false);
  };

  const salvaRiv = async () => {
    if (!riv.form.motivazione.trim()) return toast.error('Indica la motivazione della rivalutazione');
    setSalvando(true);
    try {
      await api.post(`/contenzioni/${riv.contenzione.id}/rivalutazioni`, riv.form);
      toast.success(riv.form.esito === 'Rimossa' ? 'Contenzione rimossa e registrata' : 'Rivalutazione registrata');
      setRiv(null); carica(); onSaved?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore nel salvataggio'); }
    setSalvando(false);
  };

  if (!dati || !defs) return null;

  return (
    <Card data-testid="contenzioni-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-base font-semibold text-slate-800 flex items-center gap-2 flex-wrap">
          <Lock className="w-4 h-4 text-brand" />Registro contenzioni
          <Badge variant="outline" className="text-[10px]" data-testid="contenzioni-attive">{dati.attive} attive</Badge>
          {puoScrivere && (
            <Button size="sm" className="h-7 text-xs gap-1 ml-auto bg-brand hover:bg-brand-dark text-white"
              onClick={() => setNuova({
                tipo: '', fascia: defs.fasce[0], motivazione: '', alternative_tentate: '',
                prescrizione_medica: false, medico_prescrittore: '', data_prescrizione: OGGI(),
                consenso_familiare: false, consenso_firmato_da: '', data_consenso: OGGI(),
                data_inizio: OGGI(), note: '',
              })} data-testid="nuova-contenzione-btn"><Plus className="w-3 h-3" />Nuova contenzione</Button>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        {dati.contenzioni.length === 0 && <p className="text-xs text-emerald-700">Nessuna contenzione registrata per questo ospite.</p>}
        {dati.contenzioni.map(c => {
          const open = aperta === c.id;
          return (
            <div key={c.id} className={`rounded-lg border ${c.stato === 'cessata' ? 'bg-slate-50' : c.conforme ? '' : 'border-red-200 bg-red-50/40'}`} data-testid={`contenzione-${c.id}`}>
              <button className="w-full flex items-center justify-between gap-3 p-3 text-left" onClick={() => setAperta(open ? null : c.id)}>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-slate-800">{c.tipo}</p>
                  <p className="text-[11px] text-slate-500">{c.fascia} · dal {it(c.data_inizio)}{c.data_fine ? ` al ${it(c.data_fine)}` : ''}</p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {c.stato === 'cessata'
                    ? <Badge className="text-[10px] bg-slate-200 text-slate-600">cessata</Badge>
                    : <>
                      {!c.prescrizione_medica && <Badge className="text-[10px] bg-red-100 text-red-700" data-testid={`senza-prescrizione-${c.id}`}>senza prescrizione</Badge>}
                      {!c.consenso_familiare && <Badge className="text-[10px] bg-red-100 text-red-700">senza consenso</Badge>}
                      {c.da_rivalutare && <Badge className="text-[10px] bg-amber-100 text-amber-700" data-testid={`da-rivalutare-${c.id}`}>da rivalutare</Badge>}
                      {c.conforme && !c.da_rivalutare && <Badge className="text-[10px] bg-emerald-100 text-emerald-700">conforme</Badge>}
                    </>}
                  <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`} />
                </div>
              </button>
              {open && (
                <div className="px-3 pb-3 space-y-2 text-xs text-slate-600">
                  <p><b>Motivazione:</b> {c.motivazione}</p>
                  {c.alternative_tentate && <p><b>Alternative tentate:</b> {c.alternative_tentate}</p>}
                  <p><b>Prescrizione medica:</b> {c.prescrizione_medica ? `${c.medico_prescrittore || 'presente'} del ${it(c.data_prescrizione)}` : 'ASSENTE'}</p>
                  <p><b>Consenso del familiare:</b> {c.consenso_familiare ? `${c.consenso_firmato_da || 'acquisito'} del ${it(c.data_consenso)}` : 'NON ACQUISITO'}</p>
                  {c.stato === 'attiva' && <p><b>Prossima rivalutazione:</b> {it(c.prossima_rivalutazione)} ({c.giorni_alla_scadenza} giorni)</p>}
                  {c.rivalutazioni.length > 0 && (
                    <div className="space-y-1">
                      <p className="font-semibold text-slate-500 uppercase tracking-wider text-[10px]">Rivalutazioni</p>
                      {c.rivalutazioni.map(r => (
                        <p key={r.id} className="text-[11px]">{it(r.data)} · {r.esito} · {r.motivazione} ({r.operatore_nome})</p>
                      ))}
                    </div>
                  )}
                  {puoScrivere && c.stato === 'attiva' && (
                    <div className="flex flex-wrap gap-2 pt-1">
                      <Button size="sm" className="h-8 text-xs gap-1 bg-brand hover:bg-brand-dark text-white"
                        onClick={() => setRiv({ contenzione: c, form: { data: OGGI(), esito: defs.esiti[0], motivazione: '', note: '' } })}
                        data-testid={`rivaluta-${c.id}`}><CalendarCheck className="w-3 h-3" />Rivaluta</Button>
                      <ModuloConsenso contenzione={c} onSaved={carica} />
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </CardContent>

      <Dialog open={!!nuova} onOpenChange={(o) => !o && setNuova(null)}>
        <DialogContent className="max-w-xl max-h-[88vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Nuova contenzione</DialogTitle></DialogHeader>
          {nuova && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs">Tipo *</Label>
                  <Select value={nuova.tipo} onValueChange={v => setNuova(n => ({ ...n, tipo: v }))}>
                    <SelectTrigger data-testid="contenzione-tipo"><SelectValue placeholder="Seleziona" /></SelectTrigger>
                    <SelectContent>{defs.tipi.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label className="text-xs">Fascia di applicazione *</Label>
                  <Select value={nuova.fascia} onValueChange={v => setNuova(n => ({ ...n, fascia: v }))}>
                    <SelectTrigger data-testid="contenzione-fascia"><SelectValue /></SelectTrigger>
                    <SelectContent>{defs.fasce.map(f => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div><Label className="text-xs">Motivazione clinica *</Label>
                <DictableTextarea rows={2} value={nuova.motivazione} onChange={e => setNuova(n => ({ ...n, motivazione: e.target.value }))} data-testid="contenzione-motivazione" /></div>
              <div><Label className="text-xs">Alternative tentate prima della contenzione</Label>
                <DictableTextarea rows={2} value={nuova.alternative_tentate} onChange={e => setNuova(n => ({ ...n, alternative_tentate: e.target.value }))} /></div>
              <div className="bg-slate-50 rounded-lg p-3 space-y-3">
                <label className="flex items-center justify-between gap-2 text-xs text-slate-600">
                  Prescrizione medica presente
                  <Switch checked={nuova.prescrizione_medica} onCheckedChange={v => setNuova(n => ({ ...n, prescrizione_medica: v }))} data-testid="contenzione-prescrizione" />
                </label>
                {nuova.prescrizione_medica && (
                  <div className="grid grid-cols-2 gap-3">
                    <Input className="h-8 text-xs" placeholder="Medico prescrittore" value={nuova.medico_prescrittore} onChange={e => setNuova(n => ({ ...n, medico_prescrittore: e.target.value }))} />
                    <DatePickerField className="h-8 text-xs" value={nuova.data_prescrizione} onChange={v => setNuova(n => ({ ...n, data_prescrizione: v }))} />
                  </div>
                )}
                <label className="flex items-center justify-between gap-2 text-xs text-slate-600">
                  Consenso informato del familiare acquisito
                  <Switch checked={nuova.consenso_familiare} onCheckedChange={v => setNuova(n => ({ ...n, consenso_familiare: v }))} data-testid="contenzione-consenso" />
                </label>
                {nuova.consenso_familiare && (
                  <div className="grid grid-cols-2 gap-3">
                    <Input className="h-8 text-xs" placeholder="Firmato da" value={nuova.consenso_firmato_da} onChange={e => setNuova(n => ({ ...n, consenso_firmato_da: e.target.value }))} />
                    <DatePickerField className="h-8 text-xs" value={nuova.data_consenso} onChange={v => setNuova(n => ({ ...n, data_consenso: v }))} />
                  </div>
                )}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs">Data di inizio *</Label>
                  <DatePickerField value={nuova.data_inizio} onChange={v => setNuova(n => ({ ...n, data_inizio: v }))} testId="contenzione-data" /></div>
                <div><Label className="text-xs">Note</Label>
                  <Input value={nuova.note} onChange={e => setNuova(n => ({ ...n, note: e.target.value }))} /></div>
              </div>
              <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2">
                La contenzione richiede prescrizione medica, consenso informato e rivalutazione almeno ogni {defs.rivalutazione_giorni} giorni.
              </p>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setNuova(null)}>Annulla</Button>
                <Button onClick={crea} disabled={salvando} className="bg-brand hover:bg-brand-dark text-white" data-testid="salva-contenzione">
                  {salvando ? 'Salvataggio...' : 'Registra contenzione'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!riv} onOpenChange={(o) => !o && setRiv(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Rivalutazione · {riv?.contenzione.tipo}</DialogTitle></DialogHeader>
          {riv && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs">Data *</Label>
                  <DatePickerField value={riv.form.data} onChange={v => setRiv(r => ({ ...r, form: { ...r.form, data: v } }))} testId="riv-data" /></div>
                <div><Label className="text-xs">Esito *</Label>
                  <Select value={riv.form.esito} onValueChange={v => setRiv(r => ({ ...r, form: { ...r.form, esito: v } }))}>
                    <SelectTrigger data-testid="riv-esito"><SelectValue /></SelectTrigger>
                    <SelectContent>{defs.esiti.map(e => <SelectItem key={e} value={e}>{e}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div><Label className="text-xs">Motivazione *</Label>
                <DictableTextarea rows={3} value={riv.form.motivazione} onChange={e => setRiv(r => ({ ...r, form: { ...r.form, motivazione: e.target.value } }))} data-testid="riv-motivazione" /></div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setRiv(null)}>Annulla</Button>
                <Button onClick={salvaRiv} disabled={salvando} className="bg-brand hover:bg-brand-dark text-white" data-testid="salva-rivalutazione-contenzione">
                  {salvando ? 'Salvataggio...' : 'Salva rivalutazione'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
}
